import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { X, Gift, Calendar } from 'lucide-react';
import axiosInstance from '../api/axios';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';

const Register = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    birthday: '',
    newsletter_consent: false,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/auth/register', {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        birthday: formData.birthday || null,
        newsletter_consent: formData.newsletter_consent,
      });
      setAuth(response.data.user, response.data.token);
      toast.success('Erfolgreich registriert!');

      const claimed = response.data.claimed_orders_count;
      if (claimed && claimed > 0) {
        toast.success(
          claimed === 1
            ? 'Eine frühere Gast-Bestellung wurde Ihrem neuen Konto zugeordnet.'
            : `${claimed} frühere Gast-Bestellungen wurden Ihrem neuen Konto zugeordnet.`,
          { duration: 7000 }
        );
      }

      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
      <Card className="w-full max-w-md relative rounded-none">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Schließen"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>

        <CardHeader>
          <CardTitle className="text-2xl" data-testid="register-title">Registrieren</CardTitle>
          <CardDescription>Erstellen Sie ein neues Konto</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">Vorname</Label>
                <Input
                  id="first_name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  data-testid="register-firstname-input"
                  className="rounded-none"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Nachname</Label>
                <Input
                  id="last_name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  data-testid="register-lastname-input"
                  className="rounded-none"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="register-email-input"
                className="rounded-none"
              />
            </div>

            {/* Birthday Field */}
            <div>
              <Label htmlFor="birthday" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Geburtsdatum (optional)
              </Label>
              <Input
                id="birthday"
                type="date"
                value={formData.birthday}
                onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                data-testid="register-birthday-input"
                className="rounded-none"
                max={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <Gift className="h-3 w-3" />
                Erhalten Sie einen Geburtstags-Gutschein!
              </p>
            </div>

            <div>
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                data-testid="register-password-input"
                className="rounded-none"
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                data-testid="register-confirm-password-input"
                className="rounded-none"
              />
            </div>

            {/* Newsletter Consent */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-none">
              <input
                type="checkbox"
                id="newsletter_consent"
                checked={formData.newsletter_consent}
                onChange={(e) => setFormData({ ...formData, newsletter_consent: e.target.checked })}
                className="mt-1"
              />
              <label htmlFor="newsletter_consent" className="text-sm text-gray-700">
                Ich möchte über Angebote, neue Produkte und exklusive Aktionen per E-Mail informiert werden.
                <span className="block text-xs text-gray-500 mt-1">
                  (Willkommens-E-Mails & Geburtstags-Gutschein nur mit Einwilligung)
                </span>
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full rounded-none text-white border-none" 
              disabled={loading}
              
            >
              {loading ? 'Registrierung läuft...' : 'Registrieren'}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <span className="text-gray-600">Bereits ein Konto? </span>
            <Link to="/login" className="text-amber-700 hover:underline font-medium">
              Jetzt anmelden
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
